import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight, PackageCheck, FileText, Check, X, Loader2,
  AlertTriangle, ShieldCheck, Clock, Ban,
} from 'lucide-react';
import { aiProposalsAPI } from '../lib/api';
import { toast } from 'sonner';

const ACTION_ICONS = {
  inventory_transfer: ArrowRight,
  return_to_inventory: PackageCheck,
  po_draft: FileText,
};

const ACTION_LABELS = {
  inventory_transfer: 'Inventory Transfer',
  return_to_inventory: 'Return to Inventory',
  po_draft: 'Purchase Order Draft',
};

const STATUS_CONFIG = {
  pending:   { label: 'Awaiting Action', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: Clock },
  approved:  { label: 'Approved',        color: 'text-blue-700 bg-blue-50 border-blue-200',     icon: ShieldCheck },
  executed:  { label: 'Completed',       color: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: Check },
  rejected:  { label: 'Declined',        color: 'text-red-600 bg-red-50 border-red-200',        icon: X },
  cancelled: { label: 'Cancelled',       color: 'text-muted-foreground bg-muted border-muted',  icon: Ban },
};

export function AIActionCard({ proposal, onUpdate }) {
  const [loading, setLoading] = useState(null);
  const Icon = ACTION_ICONS[proposal.action_type] || FileText;
  const label = ACTION_LABELS[proposal.action_type] || proposal.action_type;
  const statusCfg = STATUS_CONFIG[proposal.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusCfg.icon;
  const payload = proposal.action_payload || {};
  const isTerminal = ['executed', 'rejected', 'cancelled'].includes(proposal.status);

  const handleAction = async (action) => {
    setLoading(action);
    try {
      let res;
      if (action === 'approve') res = await aiProposalsAPI.approve(proposal.id);
      else if (action === 'execute') res = await aiProposalsAPI.execute(proposal.id);
      else if (action === 'reject') res = await aiProposalsAPI.reject(proposal.id);
      else if (action === 'cancel') res = await aiProposalsAPI.cancel(proposal.id);
      const verbs = { approve: 'Approved', execute: 'Executed', reject: 'Declined', cancel: 'Cancelled' };
      toast.success(`${label} ${verbs[action] || action}`);
      onUpdate?.(res?.data || proposal);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} proposal`);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div
      className={`mt-2.5 rounded-xl border transition-all ${
        isTerminal
          ? 'bg-muted/20 border-border/50 opacity-75'
          : 'bg-white border-emerald-200/80 shadow-sm'
      }`}
      data-testid={`ai-action-card-${proposal.id}`}
    >
      {/* Header bar */}
      <div className={`flex items-center justify-between px-3 py-2 rounded-t-xl border-b ${
        isTerminal ? 'bg-muted/30 border-border/50' : 'bg-emerald-50/60 border-emerald-100'
      }`}>
        <div className="flex items-center gap-2">
          <Icon className={`w-3.5 h-3.5 ${isTerminal ? 'text-muted-foreground' : 'text-emerald-700'}`} />
          <span className={`text-xs font-semibold ${isTerminal ? 'text-muted-foreground' : 'text-foreground'}`}>{label}</span>
          {proposal.requires_approval && !isTerminal && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full border border-amber-300 text-amber-700 bg-amber-50">
              <AlertTriangle className="w-2.5 h-2.5" /> Approval Required
            </span>
          )}
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusCfg.color}`}>
          <StatusIcon className="w-2.5 h-2.5" /> {statusCfg.label}
        </span>
      </div>

      {/* Body */}
      <div className="px-3 py-2.5 space-y-2">
        {/* Items table */}
        {payload.items?.length > 0 && (
          <div className="rounded-lg overflow-hidden border border-border/50">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/40">
                  <th className="text-left font-medium text-muted-foreground px-2.5 py-1.5">Item</th>
                  <th className="text-right font-medium text-muted-foreground px-2.5 py-1.5 w-16">Qty</th>
                  {payload.items.some(i => i.unit_cost != null) && (
                    <th className="text-right font-medium text-muted-foreground px-2.5 py-1.5 w-20">Unit Cost</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {payload.items.map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-2.5 py-1.5 font-medium">{item.item_name || item.item_id?.slice(0, 12)}</td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums">{item.quantity}</td>
                    {payload.items.some(i => i.unit_cost != null) && (
                      <td className="px-2.5 py-1.5 text-right tabular-nums text-muted-foreground">
                        {item.unit_cost != null ? `$${Number(item.unit_cost).toFixed(2)}` : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Location flow */}
        {(proposal.action_type === 'inventory_transfer' || proposal.action_type === 'return_to_inventory') && (
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium px-2 py-1 rounded bg-muted">{payload.from_location_name || payload.from_van_name || 'Source'}</span>
            <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
            <span className="font-medium px-2 py-1 rounded bg-muted">{payload.to_location_name || payload.to_warehouse_name || 'Destination'}</span>
          </div>
        )}

        {/* PO vendor/warehouse */}
        {proposal.action_type === 'po_draft' && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {payload.vendor_name && (
              <span className="text-muted-foreground">Vendor: <span className="font-medium text-foreground">{payload.vendor_name}</span></span>
            )}
            {payload.warehouse_name && (
              <span className="text-muted-foreground">Ship to: <span className="font-medium text-foreground">{payload.warehouse_name}</span></span>
            )}
          </div>
        )}

        {/* Execution result */}
        {proposal.status === 'executed' && proposal.execution_result && (
          <div className="flex items-start gap-1.5 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-2">
            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              {proposal.execution_result.po_number
                ? `PO #${proposal.execution_result.po_number} created — $${Number(proposal.execution_result.total_amount).toFixed(2)}`
                : proposal.execution_result.count
                  ? `${proposal.execution_result.count} item(s) transferred successfully`
                  : 'Action completed successfully'}
            </span>
          </div>
        )}

        {/* Action buttons */}
        {!isTerminal && (
          <div className="flex items-center gap-2 pt-1" data-testid={`ai-action-buttons-${proposal.id}`}>
            {proposal.status === 'pending' && !proposal.requires_approval && (
              <Button
                size="sm"
                className="h-7 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => handleAction('execute')}
                disabled={!!loading}
                data-testid={`ai-action-confirm-${proposal.id}`}
              >
                {loading === 'execute' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                Confirm
              </Button>
            )}
            {proposal.status === 'pending' && proposal.requires_approval && (
              <Button
                size="sm"
                className="h-7 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => handleAction('approve')}
                disabled={!!loading}
                data-testid={`ai-action-approve-${proposal.id}`}
              >
                {loading === 'approve' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                Approve
              </Button>
            )}
            {proposal.status === 'approved' && (
              <Button
                size="sm"
                className="h-7 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => handleAction('execute')}
                disabled={!!loading}
                data-testid={`ai-action-execute-${proposal.id}`}
              >
                {loading === 'execute' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                Execute Now
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => handleAction('reject')}
              disabled={!!loading}
              data-testid={`ai-action-reject-${proposal.id}`}
            >
              {loading === 'reject' && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              Decline
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-muted-foreground"
              onClick={() => handleAction('cancel')}
              disabled={!!loading}
              data-testid={`ai-action-cancel-${proposal.id}`}
            >
              Dismiss
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AIActionCard;
